<template>
  <div>
    <Header title="Gestion des directions" />
    <div class="directions-view">
      <div class="container">
        <!-- En-tête avec bouton retour -->
        <div class="view-header">
          <router-link to="/parametres" class="back-button">
            <span class="material-icons">arrow_back</span>
            Retour aux paramètres
          </router-link>
          <h1>Gestion des directions</h1>
          <div class="header-actions">
            <button class="btn btn-primary btn-with-icon" @click="openAddDirectionModal">
              <span class="material-icons">add_circle</span>
              <span>Nouvelle direction</span>
            </button>
          </div>
        </div>

        <!-- Section de recherche -->
        <div class="search-section">
          <div class="search-input">
            <span class="material-icons">search</span>
            <input 
              type="text" 
              v-model="searchQuery" 
              placeholder="Rechercher une direction..." 
              @input="filterDirections"
            />
          </div>
        </div>

      </div>
      
      <div class="directions-content">
        <!-- Liste des directions -->
        <div class="content-container">
          <div v-if="loading" class="loading-indicator">
            <div class="spinner"></div>
            <p>Chargement des directions...</p>
          </div>
          
          <div v-else-if="filteredDirections.length === 0" class="no-results">
            <span class="material-icons">search_off</span>
            <p v-if="searchQuery">Aucune direction trouvée pour la recherche "{{ searchQuery }}"</p>
            <p v-else>Aucune direction disponible. Veuillez en ajouter une.</p>
          </div>

          <div v-else class="directions-list">
            <div
              v-for="direction in filteredDirections"
              :key="direction.id"
              class="direction-item"
              :class="{ 'selected': selectedDirection && selectedDirection.id === direction.id }"
              @click="selectDirection(direction)"
            >
              <div class="direction-info">
                <span class="direction-icon material-icons">account_balance</span>
                <div class="direction-details">
                  <h3>{{ direction.nom }}</h3>
                  <div class="responsable-info" v-if="direction.chef && direction.chef.nom">
                    <span class="material-icons responsable-icon">supervisor_account</span>
                    <div class="responsable-details">
                      <span class="responsable-label">Responsable:</span>
                      <span class="responsable-name">{{ direction.chef.nom }}</span>
                    </div>
                  </div>
                  <div class="responsable-info no-responsable" v-else>
                    <span class="material-icons responsable-icon">person_off</span>
                    <span class="no-responsable-text">Sans responsable assigné</span>
                  </div>
                </div>
              </div>
              <div class="direction-actions">
                <button class="btn-icon btn-edit" @click.stop="editDirection(direction)" title="Modifier cette direction">
                  <span class="material-icons">edit</span>
                </button>
                <button class="btn-icon btn-delete" @click.stop="confirmDeleteDirection(direction)" title="Supprimer cette direction">
                  <span class="material-icons">delete</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Détails de la direction sélectionnée -->
        <div v-if="selectedDirection" class="direction-details-panel">
          <div class="panel-header">
            <h3>Détails de la direction</h3>
            <button class="btn-close" @click="selectedDirection = null">
              <span class="material-icons">close</span>
            </button>
          </div>
          <div class="panel-content">
            <div class="detail-field">
              <span class="field-label">Nom:</span>
              <span class="field-value">{{ selectedDirection.nom }}</span>
            </div>
            <div class="detail-field">
              <span class="field-label">Responsable:</span>
              <div class="field-value" v-if="selectedDirection.chef">
                <div class="responsable-card">
                  <span class="material-icons responsable-card-icon">person</span>
                  <div class="responsable-card-info">
                    <div class="responsable-card-name">{{ selectedDirection.chef.nom }}</div>
                    <div class="responsable-card-details">
                      <span class="responsable-card-sexe">{{ selectedDirection.chef.sexe }}</span>
                      <span class="responsable-card-separator">•</span>
                      <span class="responsable-card-matricule">{{ selectedDirection.chef.matricule }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <span class="field-value no-responsable-assigned" v-else>
                <span class="material-icons">person_off</span>
                Non assigné
              </span>
            </div>
            <div class="detail-field">
              <span class="field-label">Créée le:</span>
              <span class="field-value">{{ formatDate(selectedDirection.createdAt) }}</span>
            </div>
            <div class="detail-field">
              <span class="field-label">Modifiée le:</span>
              <span class="field-value">{{ formatDate(selectedDirection.updatedAt) }}</span>
            </div>
          </div>
          
          <!-- Liste des agents dans cette direction -->
          <div class="panel-section">
            <h4>Agents de la direction</h4>
            <div v-if="loadingAgents" class="loading-indicator">
              <div class="spinner"></div>
              <p>Chargement des agents...</p>
            </div>
            <div v-else-if="!associatedAgents.length" class="no-results">
              <p>Aucun agent n'est associé à cette direction</p>
            </div>
            <div v-else class="agents-container">
              <!-- Barre de recherche pour les agents -->
              <div class="agents-search">
                <div class="search-input-small">
                  <span class="material-icons">search</span>
                  <input 
                    type="text" 
                    v-model="agentSearchQuery" 
                    placeholder="Rechercher un agent..." 
                    @input="filterAgents"
                  />
                  <button 
                    v-if="agentSearchQuery" 
                    class="clear-search" 
                    @click="agentSearchQuery = ''; filterAgents()"
                    title="Effacer la recherche"
                  >
                    <span class="material-icons">clear</span>
                  </button>
                </div>
              </div>
              
              <!-- Message si aucun résultat de recherche -->
              <div v-if="agentSearchQuery && filteredAgents.length === 0" class="no-search-results">
                <span class="material-icons">person_search</span>
                <p>Aucun agent trouvé pour "{{ agentSearchQuery }}"</p>
              </div>
              
              <!-- Liste des agents filtrés -->
              <div v-else class="agents-list">
                <div v-for="agent in filteredAgents" :key="agent.num_personnel || agent.matricule" class="agent-item" @click="viewAgent(agent.num_personnel)">
                <div class="agent-info">
                  <div class="agent-avatar">
                    <img v-if="agent.photo" :src="agent.photo" alt="Photo de profil" class="agent-photo" />
                    <span v-else class="material-icons agent-icon">person</span>
                  </div>
                  <div class="agent-details">
                    <div class="agent-name">{{ agent.nom }}</div>
                    <div class="agent-matricule">{{ agent.matricule }}</div>
                    <div class="agent-metadata" v-if="agent.email">
                      <span class="agent-grade">Email : {{ agent.email }}</span>
                    </div>
                  </div>
                  
                </div>
                <div class="agent-actions">
                  <div class="redirection-indicator">
                    <span class="material-icons">navigate_next</span>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal pour l'ajout/modification de direction -->
    <dialog ref="directionModal" class="direction-modal">
      <div class="modal-header">
        <h3>{{ currentAction === 'create' ? 'Ajouter une direction' : 'Modifier la direction' }}</h3>
        <button class="btn-close" @click="closeModal">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="saveDirection" id="directionForm">
          <div class="form-group">
            <label for="code">Code de la direction*</label>
            <input 
              type="text" 
              id="code" 
              v-model="formData.code" 
              required 
              :class="{ 'error': formErrors.code }"
              placeholder="Ex: DSI, DRH, etc."
              maxlength="10"
            />
            <span class="error-message" v-if="formErrors.code">{{ formErrors.code }}</span>
            <p class="form-help-text">Code court identifiant la direction (max 10 caractères)</p>
          </div>
          <div class="form-group">
            <label for="nom">Nom de la direction*</label>
            <input 
              type="text" 
              id="nom" 
              v-model="formData.nom" 
              required 
              :class="{ 'error': formErrors.nom }"
              placeholder="Ex: Direction des Systèmes d'Information"
            />
            <span class="error-message" v-if="formErrors.nom">{{ formErrors.nom }}</span>
          </div>
          <div class="form-group">
            <label for="responsable_id">Responsable</label>
            <div class="select-wrapper">
              <span class="material-icons select-icon">person_search</span>
              <select 
                id="responsable_id" 
                v-model="formData.chef_direction"
                :class="{ 'error': formErrors.chef_direction }"
              >
                <option value="">-- Sélectionner un responsable --</option>
                <option 
                  v-for="personnel in personnelList" 
                  :key="personnel.num_personnel" 
                  :value="personnel.num_personnel"
                >
                  {{ personnel.nom }} {{ personnel.matricule || personnel.num_personnel ? `(${personnel.matricule || personnel.num_personnel})` : '' }}
                </option>
              </select>
            </div>
            <span class="error-message" v-if="formErrors.chef_direction">{{ formErrors.chef_direction }}</span>
            <p class="form-help-text">Sélectionnez l'agent qui sera responsable de cette direction</p>
          </div>
        </form>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" @click="closeModal" :disabled="savingDirection">Annuler</button>
        <button class="btn btn-primary" form="directionForm" :disabled="savingDirection" :class="{ 'loading': savingDirection }">
          <div v-if="savingDirection" class="btn-spinner"></div>
          <span v-if="!savingDirection">{{ currentAction === 'create' ? 'Ajouter' : 'Enregistrer' }}</span>
          <span v-else>{{ currentAction === 'create' ? 'Ajout en cours...' : 'Enregistrement...' }}</span>
        </button>
      </div>
    </dialog>

    <!-- Modal de confirmation de suppression -->
    <dialog ref="confirmDeleteModal" class="confirm-modal">
      <div class="modal-header">
        <h3>Confirmer la suppression</h3>
        <button class="btn-close" @click="closeConfirmModal">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body">
        <p>Êtes-vous sûr de vouloir supprimer la direction "{{ directionToDelete?.nom }}" ?</p>
        <p class="warning">Cette action est irréversible et peut affecter tous les agents associés.</p>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" @click="closeConfirmModal">Annuler</button>
        <button class="btn btn-danger" @click="deleteDirection">Supprimer</button>
      </div>
    </dialog>

    <!-- Notification toast -->
    <div 
      v-if="notification.show" 
      class="notification" 
      :class="notification.type"
    >
      <div class="notification-icon">
        <span class="material-icons">
          {{ notification.type === 'success' ? 'check_circle' : 
             notification.type === 'error' ? 'error' : 
             notification.type === 'info' ? 'info' : 'warning' }}
        </span>
      </div>
      <div class="notification-content">
        <h4>{{ notification.title }}</h4>
        <p>{{ notification.message }}</p>
      </div>
      <button class="notification-close" @click="notification.show = false">
        <span class="material-icons">close</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import api from '../services/api';
import Header from '../components/Header.vue';

// Router pour la navigation
const router = useRouter();

// États réactifs
const directions = ref([]);
const filteredDirections = ref([]);
const selectedDirection = ref(null);
const associatedAgents = ref([]);
const personnelList = ref([]);
const loading = ref(false);
const loadingAgents = ref(false);
const savingDirection = ref(false);
const searchQuery = ref('');
const agentSearchQuery = ref('');
const filteredAgents = ref([]);

// Modals
const directionModal = ref(null);
const confirmDeleteModal = ref(null);

// État du formulaire
const formData = reactive({
  code: '',
  nom: '',
  chef_direction: ''
});
const formErrors = reactive({
  code: '',
  nom: '',
  chef_direction: ''
});

// État de l'action courante (create, update, delete)
const currentAction = ref('create');
const directionToDelete = ref(null);

// Notification
const notification = reactive({
  show: false,
  type: 'info',
  title: '',
  message: '',
  duration: 5000
});

// Charger les directions au montage du composant
onMounted(async () => {
  await loadDirections();
  await loadPersonnel();
});

// Méthodes
async function loadDirections() {
  loading.value = true;
  try {
    const response = await api.getDirections();
    if (response.data && response.data.success) {
      directions.value = response.data.data || [];
      filteredDirections.value = [...directions.value];
    }
  } catch (error) {
    console.error('Erreur lors du chargement des directions:', error);
    showNotification('error', 'Erreur', 'Impossible de charger les directions.');
  } finally {
    loading.value = false;
  }
}

async function loadPersonnel() {
  try {
    const response = await api.get('/api/personnel-liste');
    if (response.data && response.data.success) {
      personnelList.value = response.data.data || [];
    }
  } catch (error) {
    console.error('Erreur lors du chargement du personnel:', error);
    showNotification('error', 'Erreur', 'Impossible de charger la liste du personnel.');
  }
}

function filterDirections() {
  if (!searchQuery.value.trim()) {
    filteredDirections.value = [...directions.value];
    return;
  }
  
  const query = searchQuery.value.toLowerCase();
  filteredDirections.value = directions.value.filter(direction => {
    const directionNameMatch = direction.nom.toLowerCase().includes(query);
    
    let responsableMatch = false;
    if (direction.chef && direction.chef.nom) {
      const responsableName = `${direction.chef.nom} ${direction.chef.prenom || ''}`.toLowerCase();
      responsableMatch = responsableName.includes(query);
    }
    
    return directionNameMatch || responsableMatch;
  });
}

async function selectDirection(direction) {
  selectedDirection.value = direction;
  await loadAssociatedAgents(direction.id);
}

async function loadAssociatedAgents(directionId) {
  loadingAgents.value = true;
  associatedAgents.value = [];
  agentSearchQuery.value = ''; // Reset search when loading new agents

  try {
    // Utilisation de l'endpoint dédié pour récupérer les agents d'une direction
    const response = await api.get(`/api/parametres/directions/${directionId}/agents`);
    
    if (response.data && response.data.success) {
      associatedAgents.value = response.data.data || [];
      filteredAgents.value = [...associatedAgents.value]; // Initialize filtered list
      
      if (associatedAgents.value.length === 0) {
        // Aucun agent trouvé pour cette direction
        showNotification('info', 'Information', 'Aucun agent associé à cette direction.');
      }
    } else {
      showNotification('warning', 'Attention', response.data.message || 'Aucun agent associé trouvé');
    }
  } catch (error) {
    console.error('Erreur lors du chargement des agents associés:', error);
    showNotification('error', 'Erreur', 'Impossible de charger les agents associés.');
  } finally {
    loadingAgents.value = false;
  }
}

function openAddDirectionModal() {
  resetForm();
  currentAction.value = 'create';
  directionModal.value.showModal();
}

function editDirection(direction) {
  resetForm();
  currentAction.value = 'update';
  console.log('Structure complète de la direction à éditer:', direction);
  formData.code = direction.code || '';
  formData.nom = direction.nom;
  formData.chef_direction = direction.chef_direction || 
                          (direction.chef && (direction.chef.num_personnel)) || 
                          '';
  // Pour debug, affichons la structure du chef pour mieux comprendre
  if (direction.chef) {
    console.log('Structure du chef de direction:', direction.chef);
  }
  formData.id = direction.id;
  directionModal.value.showModal();
}

function confirmDeleteDirection(direction) {
  directionToDelete.value = direction;
  confirmDeleteModal.value.showModal();
}

function closeModal() {
  directionModal.value.close();
}

function closeConfirmModal() {
  confirmDeleteModal.value.close();
}

function resetForm() {
  formData.id = null;
  formData.code = '';
  formData.nom = '';
  formData.chef_direction = '';
  Object.keys(formErrors).forEach(key => formErrors[key] = '');
}

async function saveDirection() {
  // Valider le formulaire
  if (!validateForm()) return;
  
  savingDirection.value = true;
  
  try {
    let response;
    
    if (currentAction.value === 'create') {
      response = await api.createDirection(formData);
      if (response.data && response.data.success) {
        showNotification('success', 'Succès', 'Direction créée avec succès');
      }
    } else {
      response = await api.updateDirection(formData.id, formData);
      if (response.data && response.data.success) {
        showNotification('success', 'Succès', 'Direction modifiée avec succès');
      }
    }
    
    await loadDirections();
    closeModal();
    
    // Si une direction est sélectionnée et qu'elle vient d'être modifiée, mettre à jour sa sélection
    if (selectedDirection.value && selectedDirection.value.id === formData.id) {
      const updatedDirection = directions.value.find(d => d.id === formData.id);
      if (updatedDirection) {
        selectedDirection.value = updatedDirection;
      }
    }
    
  } catch (error) {
    console.error('Erreur lors de la sauvegarde de la direction:', error);
    showNotification('error', 'Erreur', `Impossible de sauvegarder la direction: ${error.message}`);
  } finally {
    savingDirection.value = false;
  }
}

async function deleteDirection() {
  if (!directionToDelete.value) return;
  
  try {
    const response = await api.deleteDirection(directionToDelete.value.id);
    if (response.data && response.data.success) {
      showNotification('success', 'Suppression', 'Direction supprimée avec succès');
      
      // Si la direction supprimée était sélectionnée, désélectionner
      if (selectedDirection.value && selectedDirection.value.id === directionToDelete.value.id) {
        selectedDirection.value = null;
      }
      
      await loadDirections();
    }
  } catch (error) {
    console.error('Erreur lors de la suppression de la direction:', error);
    showNotification('error', 'Erreur', `Impossible de supprimer la direction: ${error.message}`);
  }
  
  closeConfirmModal();
}

function validateForm() {
  let isValid = true;
  
  // Validation du code
  if (!formData.code.trim()) {
    formErrors.code = 'Le code est requis';
    isValid = false;
  } else if (formData.code.trim().length > 10) {
    formErrors.code = 'Le code ne doit pas dépasser 10 caractères';
    isValid = false;
  } else {
    formErrors.code = '';
  }
  
  // Validation du nom
  if (!formData.nom.trim()) {
    formErrors.nom = 'Le nom est requis';
    isValid = false;
  } else {
    formErrors.nom = '';
  }
  
  return isValid;
}

function showNotification(type, title, message) {
  notification.show = true;
  notification.type = type;
  notification.title = title;
  notification.message = message;
  
  // Masquer automatiquement après la durée spécifiée
  setTimeout(() => {
    notification.show = false;
  }, notification.duration);
}

function viewAgent(num_personnel) {
  router.push(`/agent/${num_personnel}`);
}

function filterAgents() {
  if (!agentSearchQuery.value.trim()) {
    filteredAgents.value = [...associatedAgents.value];
    return;
  }
  
  const query = agentSearchQuery.value.toLowerCase();
  filteredAgents.value = associatedAgents.value.filter(agent => {
    const nameMatch = agent.nom && agent.nom.toLowerCase().includes(query);
    const matriculeMatch = agent.matricule && agent.matricule.toLowerCase().includes(query);
    const emailMatch = agent.email && agent.email.toLowerCase().includes(query);
    
    return nameMatch || matriculeMatch || emailMatch;
  });
}

function formatDate(dateString) {
  if (!dateString) return 'N/A';
  
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
}
</script>

<style scoped>
.directions-view {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: calc(100vh - 60px);
}

.container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.directions-content {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  min-height: calc(100vh - 280px);
  align-items: start;
}

@media (min-width: 992px) {
  .directions-content {
    grid-template-columns: 2fr 1fr;
  }
}

/* En-tête et navigation */
.view-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e0e6ed;
}

.view-header h1 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #3c4043;
}

.back-button {
  display: flex;
  align-items: center;
  color: var(--turquoise-primary);
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
}

.back-button:hover {
  color: var(--turquoise-deep);
  transform: translateX(-3px);
}

.back-button .material-icons {
  margin-right: 6px;
  font-size: 18px;
}

.header-actions {
  display: flex;
  gap: 10px;
}

/* Bouton avec icône */
.btn-with-icon {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.3s ease;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
  cursor: pointer;
}

.btn-primary {
  background-color: var(--turquoise-primary);
  color: white;
  border: none;
}

.btn-primary:hover {
  background-color: var(--turquoise-deep);
  box-shadow: 0 5px 12px rgba(21, 87, 81, 0.3);
  transform: translateY(-2px);
}

.back-button .material-icons {
  margin-right: 5px;
}

.header-actions {
  display: flex;
  gap: 10px;
}

/* Section de recherche */
.search-section {
  margin-bottom: 20px;
  width: 100%;
}

.search-input {
  display: flex;
  align-items: center;
  background-color: white;
  border-radius: 8px;
  padding: 12px 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
  transition: all 0.3s ease;
}

.search-input:focus-within {
  box-shadow: 0 4px 12px rgba(48, 199, 181, 0.15);
}

.search-input .material-icons {
  color: var(--turquoise-primary);
  margin-right: 10px;
}

.search-input input {
  border: none;
  background: transparent;
  flex: 1;
  font-size: 0.95rem;
  outline: none;
  color: #3c4043;
}

.search-input input::placeholder {
  color: #9aa0a6;
}

/* Liste des directions */
.content-container {
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.directions-list {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.direction-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e6ed;
  background-color: white;
  cursor: pointer;
  transition: all 0.25s ease;
}

.direction-item:hover {
  background-color: #f8f9fc;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  z-index: 1;
}

.direction-item.selected {
  background-color: var(--turquoise-pale);
  border-left: 4px solid var(--turquoise-primary);
}

.direction-info {
  display: flex;
  align-items: center;
  gap: 15px;
}

.direction-icon {
  color: var(--turquoise-primary);
  font-size: 24px;
}

.direction-details h3 {
  margin: 0 0 5px 0;
  font-size: 16px;
  font-weight: 500;
  color: #3c4043;
}

.direction-details p {
  margin: 0;
  font-size: 14px;
  color: #5f6368;
}

.direction-info {
  display: flex;
  align-items: center;
}

.direction-icon {
  color: var(--turquoise-primary);
  margin-right: 15px;
  font-size: 24px;
}

.direction-details h3 {
  margin: 0 0 5px;
  font-size: 16px;
  font-weight: 500;
}

.responsable-info {
  display: flex;
  align-items: center;
  margin-top: 5px;
}

.responsable-icon {
  font-size: 20px;
  color: var(--turquoise-primary);
  margin-right: 6px;
}

.responsable-details {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px;
}

.responsable-label {
  font-size: 13px;
  color: #5f6368;
  font-weight: 500;
}

.responsable-name {
  font-size: 14px;
  color: #3c4043;
  font-weight: 500;
}

.responsable-matricule {
  font-size: 12px;
  color: #5f6368;
  font-style: italic;
}

.no-responsable {
  color: #9aa0a6;
}

.no-responsable .responsable-icon {
  color: #9aa0a6;
}

.no-responsable-text {
  font-size: 13px;
  font-style: italic;
}

.direction-actions {
  display: flex;
  gap: 8px;
}

.btn-icon {
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: #5f6368;
}

.btn-icon:hover {
  background-color: rgba(0, 0, 0, 0.06);
  transform: translateY(-2px);
}

.btn-icon .material-icons {
  font-size: 20px;
}

.btn-edit {
  color: var(--turquoise-primary);
}

.btn-edit:hover {
  background-color: rgba(48, 199, 181, 0.1);
}

.btn-delete {
  color: var(--coral-soft);
}

.btn-delete:hover {
  background-color: rgba(255, 122, 104, 0.1);
}

/* Panneau de détails */
.direction-details-panel {
  background-color: #f9f9f9;
  border-radius: 8px;
  overflow: hidden;
  height: fit-content;
  max-height: calc(100vh - 280px);
  display: flex;
  flex-direction: column;
}

/* Ajustement de la hauteur quand il y a des agents */
.direction-details-panel:has(.agents-list:not(:empty)) {
  max-height: calc(100vh - 50px);
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: var(--turquoise-pale);
  padding: 15px;
  border-bottom: 1px solid #ddd;
}

.panel-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
}

.panel-content {
  padding: 20px;
  flex-shrink: 0;
}

.detail-field {
  margin-bottom: 12px;
}

.field-label {
  display: block;
  font-size: 14px;
  color: #777;
  margin-bottom: 3px;
}

.field-value {
  font-size: 16px;
  color: #333;
}

.panel-section {
  padding: 20px;
  border-top: 1px solid #ddd;
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.panel-section h4 {
  margin: 0 0 15px;
  font-size: 16px;
  font-weight: 500;
}

.agents-container {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
}

.agents-search {
  margin-bottom: 15px;
}

.search-input-small {
  display: flex;
  align-items: center;
  background-color: white;
  border-radius: 6px;
  padding: 8px 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e6ed;
  transition: all 0.3s ease;
  position: relative;
}

.search-input-small:focus-within {
  border-color: var(--turquoise-primary);
  box-shadow: 0 2px 8px rgba(48, 199, 181, 0.15);
}

.search-input-small .material-icons {
  color: var(--turquoise-primary);
  margin-right: 8px;
  font-size: 18px;
}

.search-input-small input {
  border: none;
  background: transparent;
  flex: 1;
  font-size: 0.9rem;
  outline: none;
  color: #3c4043;
}

.search-input-small input::placeholder {
  color: #9aa0a6;
  font-size: 0.85rem;
}

.clear-search {
  background: none;
  border: none;
  cursor: pointer;
  color: #9aa0a6;
  padding: 2px;
  border-radius: 50%;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.clear-search:hover {
  background-color: rgba(0, 0, 0, 0.1);
  color: #5f6368;
}

.clear-search .material-icons {
  font-size: 16px;
}

.no-search-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px 20px;
  text-align: center;
  color: #9aa0a6;
  font-style: italic;
  background-color: #f8f9fc;
  border-radius: 8px;
  border: 1px dashed #dadce0;
}

.no-search-results .material-icons {
  font-size: 36px;
  margin-bottom: 10px;
  color: #bdbdbd;
}

.no-search-results p {
  margin: 0;
  font-size: 14px;
}

.agents-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  overflow-y: auto;
  min-height: 200px;
  max-height: 350px;
}

.agent-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(60, 64, 67, 0.08);
  transition: all 0.2s ease;
  border-left: 3px solid transparent;
  cursor: pointer;
  position: relative;
}

.agent-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(60, 64, 67, 0.12);
  border-left-color: var(--turquoise-primary);
  background-color: #f8f9fc;
}

.redirection-indicator {
  display: flex;
  align-items: center;
  color: var(--turquoise-primary);
  opacity: 0.7;
  transition: opacity 0.2s ease;
  margin-left: 12px;
}

.agent-item:hover .redirection-indicator {
  opacity: 1;
}

.agent-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.agent-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--turquoise-pale);
}

.agent-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.agent-icon {
  font-size: 24px;
  color: var(--turquoise-primary);
}

.agent-icon.female {
  color: #ec407a;
}

.agent-details {
  display: flex;
  flex-direction: column;
}

.agent-name {
  font-weight: 500;
  color: #3c4043;
  font-size: 14px;
  margin-bottom: 2px;
}

.agent-matricule {
  color: #5f6368;
  font-size: 12px;
  margin-bottom: 2px;
}

.agent-metadata {
  display: flex;
  gap: 5px;
  font-size: 12px;
  color: #5f6368;
}

.separator {
  color: #bdbdbd;
}

.agent-grade, .agent-fonction {
  font-style: italic;
}

.agent-actions {
  display: flex;
  gap: 8px;
}

.badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
}

.gender-badge {
  background-color: var(--turquoise-pale);
  color: var(--turquoise-deep);
}

.gender-badge.female {
  background-color: #fce4ec;
  color: #d81b60;
}

.gender-badge.male {
  background-color: var(--turquoise-pale);
  color: var(--turquoise-deep);
}

/* Indicateur de chargement et messages */
.loading-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: #5f6368;
  text-align: center;
}

.spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(48, 199, 181, 0.2);
  border-top-color: var(--turquoise-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

.loading-indicator p {
  color: #5f6368;
  font-size: 14px;
  margin-top: 5px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.no-results {
  display: flex;
  flex-direction: column;
  padding: 40px;
  text-align: center;
  color: #777;
  font-style: italic;
  background-color: #f9f9f9;
  border-radius: 8px;
  border: 1px dashed #dadce0;
  align-items: center;
  justify-content: center;
  min-height: 80px;
}

.no-results .material-icons {
  font-size: 48px;
  margin-bottom: 15px;
  color: #aaa;
}

/* Modal */
.direction-modal, .confirm-modal {
  border: none;
  border-radius: 12px;
  padding: 0;
  width: 500px;
  max-width: 95vw;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25), 0 5px 15px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  margin: 0;
  animation: fadeIn 0.3s ease-out;
}

.direction-modal::before, .confirm-modal::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(to right, var(--turquoise-deep), var(--turquoise-primary));
}

.direction-modal::backdrop, .confirm-modal::backdrop {
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  border-bottom: 1px solid #e0e6ed;
  background: linear-gradient(to right, var(--turquoise-deep), var(--turquoise-primary));
  color: white;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: white;
}

.modal-body {
  padding: 24px;
  max-height: 70vh;
  overflow-y: auto;
}

.modal-footer {
  padding: 16px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  border-top: 1px solid #e0e6ed;
  background-color: #f8f9fc;
  position: sticky;
  bottom: 0;
  z-index: 10;
}

.btn-close {
  background: none;
  border: none;
  cursor: pointer;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.btn-close:hover {
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
  transform: rotate(90deg);
}

.btn {
  padding: 10px 16px;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 100px;
}

.btn-primary {
  background-color: var(--turquoise-primary);
  color: white;
}

.btn-secondary {
  background-color: #f1f3f4;
  color: #5f6368;
  border: 1px solid #dadce0;
}

.btn-danger {
  background-color: var(--coral-soft);
  color: white;
}

.btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.btn-primary:hover {
  background-color: var(--turquoise-deep);
}

.btn-secondary:hover {
  background-color: #e8eaed;
}

.btn-danger:hover {
  background-color: #d32f2f;
}

/* Formulaire */
.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #3c4043;
  font-size: 0.9rem;
}

.select-wrapper {
  position: relative;
  display: flex;
}

.select-icon {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 12px;
  color: var(--turquoise-primary);
  pointer-events: none;
  z-index: 1;
}

.select-wrapper select {
  padding-left: 40px !important;
}

.form-help-text {
  margin-top: 6px;
  font-size: 0.8rem;
  color: #5f6368;
  font-style: italic;
}

.form-group input, .form-group select, .form-group textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #dadce0;
  border-radius: 6px;
  font-size: 0.95rem;
  color: #3c4043;
  background-color: #fff;
  transition: all 0.3s ease;
  box-shadow: 0 1px 2px rgba(60, 64, 67, 0.1);
}

.form-group input::placeholder, .form-group textarea::placeholder {
  color: #9aa0a6;
}

.form-group input:hover, .form-group select:hover, .form-group textarea:hover {
  border-color: #a0a0a0;
}

.form-group input:focus, .form-group select:focus, .form-group textarea:focus {
  outline: none;
  border-color: var(--turquoise-primary);
  box-shadow: 0 1px 6px rgba(48, 199, 181, 0.2);
}

.form-group input.error, .form-group select.error, .form-group textarea.error {
  border-color: var(--coral-soft);
  background-color: #fff8f8;
}

.error-message {
  color: var(--coral-soft);
  font-size: 0.8rem;
  margin-top: 6px;
  display: block;
}

.warning {
  background-color: #fff3e0;
  border-left: 4px solid var(--coral-soft);
  padding: 10px 15px;
  margin-top: 10px;
  border-radius: 4px;
  color: #e65100;
  font-size: 0.9rem;
}

/* Boutons */
.btn {
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  border: none;
  transition: background-color 0.2s;
}

.btn .material-icons {
  font-size: 20px;
}

.btn-primary {
  background-color: var(--turquoise-primary);
  color: white;
}

.btn-primary:hover {
  background-color: var(--turquoise-deep);
}

.btn-secondary {
  background-color: #f5f5f5;
  color: #333;
}

.btn-secondary:hover {
  background-color: #e0e0e0;
}

.btn-danger {
  background-color: var(--coral-soft);
  color: white;
}

.btn-danger:hover {
  background-color: #d32f2f;
}

/* Notification */
.notification {
  position: fixed;
  bottom: 20px;
  right: 20px;
  min-width: 300px;
  max-width: 400px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 15px;
  display: flex;
  align-items: flex-start;
  gap: 15px;
  z-index: 1000;
}

.notification.success {
  border-left: 4px solid var(--turquoise-primary);
}

.notification.error {
  border-left: 4px solid var(--coral-soft);
}

.notification.warning {
  border-left: 4px solid #FF9800;
}

.notification.info {
  border-left: 4px solid var(--turquoise-deep);
}

.notification-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification.success .material-icons {
  color: var(--turquoise-primary);
}

.notification.error .material-icons {
  color: var(--coral-soft);
}

.notification.warning .material-icons {
  color: #FF9800;
}

.notification.info .material-icons {
  color: var(--turquoise-deep);
}

.notification-content {
  flex: 1;
}

.notification-content h4 {
  margin: 0 0 5px;
  font-size: 16px;
  font-weight: 600;
}

.notification-content p {
  margin: 0;
  font-size: 14px;
  color: #555;
}

.notification-close {
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: -5px;
}

.notification-close:hover {
  color: #333;
}

.warning {
  color: #d32f2f;
  font-weight: 500;
}

/* Personnalisation des barres de défilement */
/* Pour Webkit (Chrome, Safari, Edge) */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: var(--turquoise-primary);
  border-radius: 4px;
  transition: background-color 0.3s ease;
}

::-webkit-scrollbar-thumb:hover {
  background: var(--turquoise-deep);
}

::-webkit-scrollbar-corner {
  background: #f1f1f1;
}

/* Pour Firefox */
* {
  scrollbar-width: thin;
  scrollbar-color: var(--turquoise-primary) #f1f1f1;
}

/* Carte responsable dans les détails */
.responsable-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background-color: #f8f9fc;
  padding: 12px 16px;
  border-radius: 8px;
  border-left: 3px solid var(--turquoise-primary);
  margin-top: 8px;
}

.responsable-card-icon {
  color: var(--turquoise-primary);
  font-size: 24px;
  background-color: white;
  padding: 8px;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.responsable-card-info {
  flex: 1;
}

.responsable-card-name {
  font-size: 16px;
  font-weight: 600;
  color: #3c4043;
  margin-bottom: 4px;
}

.responsable-card-details {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #5f6368;
}

.responsable-card-sexe {
  font-weight: 500;
}

.responsable-card-separator {
  color: #9aa0a6;
  font-weight: bold;
}

.responsable-card-matricule {
  font-family: monospace;
  background-color: #e8f0fe;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 13px;
  color: var(--turquoise-deep);
}

.no-responsable-assigned {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #9aa0a6;
  font-style: italic;
  margin-top: 8px;
}

.no-responsable-assigned .material-icons {
  font-size: 20px;
}

/* Styles pour l'effet de chargement du bouton */
.btn.loading {
  position: relative;
  color: rgba(255, 255, 255, 0.7) !important;
  cursor: not-allowed;
  pointer-events: none;
}

.btn-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: btn-spin 0.8s linear infinite;
  margin-right: 8px;
  display: inline-block;
}

@keyframes btn-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}

.btn:disabled:hover {
  transform: none;
  box-shadow: none;
}
</style>
